/**
 * Shared CRUD form for uplines and clients. Steps 2 and 3 render this with
 * different role + labels.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Party, PartyRole } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';

export function StepParties({
  role,
  title,
  description,
  hint,
  minCount,
}: {
  role: PartyRole;
  title: string;
  description: string;
  hint: string;
  minCount: number;
}) {
  const qc = useQueryClient();
  const { data: parties, isLoading } = useQuery<Party[]>({
    queryKey: ['parties', role],
    queryFn: () => api.get<Party[]>(`/api/parties?role=${role}`),
  });
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (body: { displayName: string; notes?: string | null }) =>
      api.post<Party>('/api/parties', { role, ...body }),
    onSuccess: () => {
      setDisplayName('');
      setNotes('');
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/parties/${id}`),
    onSuccess: () => void qc.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <Card>
      <header className="mb-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-1 text-caption text-ink-muted">{description}</p>
      </header>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          const name = displayName.trim();
          if (!name) {
            setError('Display name is required.');
            return;
          }
          createMut.mutate({ displayName: name, notes: notes.trim() || null });
        }}
      >
        <TextInput
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          name="displayName"
          placeholder={role === 'upline' ? 'e.g. SRB' : 'e.g. Gori Bhaiya'}
          hint={hint}
        />
        <TextArea
          label="Notes (optional)"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <Button type="submit" disabled={createMut.isPending}>
          {createMut.isPending ? <Spinner /> : `Add ${role}`}
        </Button>
      </form>

      <div className="mt-6">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-small font-medium text-ink">Existing</h3>
          <Pill tone={(parties?.length ?? 0) >= minCount ? 'green' : 'amber'}>
            {(parties?.length ?? 0)}/{minCount} minimum
          </Pill>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading…
          </div>
        ) : parties && parties.length > 0 ? (
          <ul className="divide-y divide-border rounded-md border border-border">
            {parties.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <span className="text-sm">{p.displayName}</span>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Delete ${p.displayName}?`)) deleteMut.mutate(p.id);
                  }}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-ink-muted">
            No {role} parties yet.
          </p>
        )}
      </div>
    </Card>
  );
}
